/**
 * Which live-database features support which engine.
 *
 * Data Dictionary import, Refresh Schema and Import Missing all work for MS SQL
 * Server — they read through the type-aware schemaController.
 *
 * Schema Monitor does NOT: schemaMonitorService still has its own
 * createRemoteConnection calling mysql.createConnection unconditionally, with
 * MySQL-dialect queries (MD5(), STATISTICS). Pointed at an MSSQL instance it fails
 * with a raw protocol error rather than anything a user can act on.
 *
 * The two reasons are therefore SEPARATE helpers. They were one, and collapsing them
 * again would silently unblock Schema Monitor the moment Refresh gained MSSQL support.
 * Delete schemaMonitorUnsupportedReason when that service grows a type-aware path.
 */

// Two vocabularies reach this. Connections carry sk_connection_type.db_conn_type_name
// ('mssql'); dictionaries carry dd_database_instances.instance_type, an ENUM whose
// member is 'SQL Server'. Neither is case-normalized. Matching only one of them is
// how the Refresh guard came to be enabled on an MS SQL dictionary.
const normalize = (engine) => String(engine || '').trim().toLowerCase();

const MSSQL_NAMES = new Set(['mssql', 'sql server', 'sqlserver', 'ms sql server', 'ms sql']);

export const isMssql = (engine) => MSSQL_NAMES.has(normalize(engine));

// Reads either an instance record (instanceType) or a connection (type/dbType).
export const engineOf = (entity) => normalize(
  entity && (entity.instanceType || entity.instance_type || entity.type || entity.dbType)
);

/**
 * Refresh Schema / Import Missing. Supported on MySQL and MS SQL Server, so this is
 * empty for both — kept as the seam for the next engine (Oracle) rather than deleted.
 *
 * @param {Object} entity  a dictionary instance or a db connection
 * @return {string} empty when supported, otherwise why it is disabled
 */
export const liveDiffUnsupportedReason = () => '';

/**
 * Schema Monitor. Still MySQL-only — see the file header.
 *
 * @param {Object} entity  a dictionary instance or a db connection
 * @return {string} empty when supported, otherwise why it is disabled
 */
export const schemaMonitorUnsupportedReason = (entity) =>
  isMssql(engineOf(entity)) ? 'Not yet supported for MS SQL Server' : '';

/**
 * What MSSQL change detection does not cover. Non-empty means "show a caveat",
 * not "disable" — the feature works, with a stated blind spot.
 *
 * @param {Object} entity  a dictionary instance
 * @return {string} empty when there is nothing to caveat
 */
export const liveDiffCaveat = (entity) =>
  isMssql(engineOf(entity)) ?
    'Change detection for MS SQL Server covers objects, columns, types, keys and ' +
    'view/procedure definitions. Auto-increment (IDENTITY) drift is not tracked.' :
    '';

export default {
  isMssql,
  engineOf,
  liveDiffUnsupportedReason,
  schemaMonitorUnsupportedReason,
  liveDiffCaveat,
};
